import React from "react";
import { useRowGestures } from "@/lib/useSelection";
import { colorIdToHex } from "@/lib/highlightColors";

/**
 * Single CCC paragraph row. Selection model identical to VerseRow:
 *   • long-press → start / re-anchor selection (resets when >1 selected).
 *   • short click → toggle (only in selection mode).
 *   • double-click → clear all.
 *
 * When the paragraph is a favourite *and* the user has tagged it with a
 * highlight colour, the row receives a pastel background that mimics a
 * physical study book. The colour layer sits BEHIND the selection
 * underline so both signals are legible at the same time.
 *
 * The body is rendered through `renderText()` so the parent can inject
 * the rich text pipeline (search highlight + cross-refs + Bible
 * citations).
 */
export default function ParagraphRow({
    paragraph, renderText, isSaved, savedColor, isSelected, inSelectionMode, isFlashing,
    onLongPress, onShortClick, onDoubleClick,
}) {
    const handlers = useRowGestures({
        inSelectionMode,
        onLongPress,
        onShortClick,
        onDoubleClick,
    });

    const bg = colorIdToHex(savedColor);

    return (
        <li
            data-ccc-id={paragraph.id}
            data-testid={`ccc-para-${paragraph.id}`}
            data-selected={isSelected ? "true" : "false"}
            data-color={savedColor || ""}
            className={[
                "ccc-row group relative rounded-md transition-colors duration-150",
                "px-3 py-3 -mx-3 cursor-pointer",
                isSaved && !bg ? "border-l-2 border-sangre/50 pl-4" : "",
                isSelected
                    ? "underline decoration-blue-600 decoration-2 underline-offset-4"
                    : "",
                isFlashing ? "ccc-flash" : "",
            ].filter(Boolean).join(" ")}
            style={{ scrollMarginTop: "80px", backgroundColor: bg || undefined }}
            {...handlers}
        >
            <div className="flex items-start gap-3">
                <span className="text-sangre font-semibold ui-sans text-sm mt-1 shrink-0">
                    §{paragraph.id}
                </span>
                <div className="flex-1 min-w-0">
                    <p className="m-0 whitespace-pre-line">{renderText()}</p>
                    {isSaved && !isSelected && (
                        <span className="sr-only" data-testid={`ccc-saved-${paragraph.id}`}>saved</span>
                    )}
                </div>
            </div>
        </li>
    );
}
